import {
    CANCEL_PAYMENT,
    CLEAR_IMAGES,
    COMPLETE_PAYMENT,
    LOCK_PAYMENT,
    LOCK_POS_PROCESSING_PAYMENT,
    SET_CASHIER_GUID,
    SET_PAYMENT_LOADING,
    SHOW_IMAGES,
    UNLOCK_PAYMENT,
    UNLOCK_POS_PROCESSING_PAYMENT,
    UPDATE_PAYMENT,
    UPDATE_PAYMENTS,
    VERIFY_PAYMENT,
} from './checkout.action';
import type { PaymentItem } from '@/checkout/checkout.service';
import { UPDATE_CUSTOMER_LABELS } from '@/sessionMonitor/sessionMonitor.action';
import type { CustomerLabel } from '@whiz-cart/node-shared/models/customer/customerLabel';
import PaymentStatus from '@whiz-cart/node-shared/paymentStatus';
import _ from 'lodash';

// Payments will be remebered, if they are still active, or if they have been updated within the last hour (to show if the payment has been canceled for example).
const retention = 24 * 60 * 60 * 1000;
const markUpdatedOn = (payment: PaymentItem) => ({ ...payment, updatedOn: new Date(), test: 'test' });
const filterActive = (payment: PaymentItem) =>
    (typeof payment.status === 'number' && payment.status < PaymentStatus.Completed) ||
    (payment.updatedOn?.getTime() ?? 0) >= Date.now() - retention;

const updatePayment = (state: CheckoutReducerState, paymentRequestGuid: string, update: Partial<PaymentItem>) => ({
    ...state,
    payments: state.payments
        .map((payment) => (payment.paymentRequestGuid === paymentRequestGuid ? { ...payment, ...update } : payment))
        .filter(filterActive),
});

const updateVerificationItem = (
    state: CheckoutReducerState,
    paymentRequestGuid: string,
    verificationGuid: string,
    update: Partial<PaymentItem>,
) => ({
    ...state,
    payments: state.payments.map((payment) =>
        payment.paymentRequestGuid === paymentRequestGuid
            ? {
                  ...payment,
                  verificationItems: payment.verificationItems?.map((item) =>
                      item.verificationItemGuid === verificationGuid ? { ...item, ...update } : item,
                  ),
              }
            : payment,
    ),
});

type CheckoutReducerState = {
    payments: PaymentItem[];
    hasLoadedOnce: boolean;
    loading: boolean;
    sessionAmount: never[];
    expressCheckout: any[];
    cashierGuid: string | undefined;
};

type CheckoutReducerParam =
    | {
          type: typeof UPDATE_PAYMENTS;
          payload: PaymentItem[];
      }
    | {
          type: typeof UPDATE_PAYMENT;
          payload: PaymentItem;
      }
    | {
          type: typeof LOCK_PAYMENT;
          payload: {
              paymentRequestGuid: string;
              userId: string;
              deviceId: string;
          };
      }
    | {
          type: typeof UNLOCK_PAYMENT;
          payload: {
              paymentRequestGuid: string;
          };
      }
    | {
          type: typeof LOCK_POS_PROCESSING_PAYMENT;
          payload: {
              paymentRequestGuid: string;
              userId: string;
              deviceId: string;
          };
      }
    | {
          type: typeof UNLOCK_POS_PROCESSING_PAYMENT;
          payload: {
              paymentRequestGuid: string;
          };
      }
    | {
          type: typeof CANCEL_PAYMENT;
          payload: {
              paymentRequestGuid: string;
          };
      }
    | {
          type: typeof COMPLETE_PAYMENT;
          payload: {
              paymentRequestGuid: string;
              posRegistrationCompletedOn: Date;
          };
      }
    | {
          type: typeof VERIFY_PAYMENT;
          payload: {
              paymentRequestGuid: string;
              verificationGuid: string;
              approved: boolean;
          };
      }
    | {
          type: typeof SET_PAYMENT_LOADING;
          payload: boolean;
      }
    | {
          type: typeof SHOW_IMAGES;
          payload: any;
      }
    | {
          type: typeof CLEAR_IMAGES;
          payload: undefined;
      }
    | {
          type: typeof SET_CASHIER_GUID;
          payload: string;
      }
    | {
          type: typeof UPDATE_CUSTOMER_LABELS;
          payload: { customerGuid: string; labels: CustomerLabel[] };
      };

export default (
    state: CheckoutReducerState = {
        payments: [],
        hasLoadedOnce: false,
        loading: true,
        sessionAmount: [],
        expressCheckout: [],
        cashierGuid: undefined,
    },
    { type, payload }: CheckoutReducerParam,
) => {
    switch (type) {
        case UPDATE_PAYMENTS:
            return { ...state, payments: payload.map(markUpdatedOn) };
        case UPDATE_PAYMENT: {
            const payments = state.payments
                .filter((p) => p.paymentRequestGuid !== payload.paymentRequestGuid)
                .concat(markUpdatedOn(payload))
                .filter(filterActive);
            return { ...state, payments };
        }
        case LOCK_PAYMENT:
            return updatePayment(state, payload.paymentRequestGuid, {
                processingBy: { userId: payload.userId, deviceId: payload.deviceId },
            });
        case UNLOCK_PAYMENT:
            return updatePayment(state, payload.paymentRequestGuid, { processingBy: null });
        case LOCK_POS_PROCESSING_PAYMENT:
            return updatePayment(state, payload.paymentRequestGuid, {
                postProcessingBy: { userId: payload.userId, deviceId: payload.deviceId },
            });
        case UNLOCK_POS_PROCESSING_PAYMENT:
            return updatePayment(state, payload.paymentRequestGuid, { postProcessingBy: null });
        case CANCEL_PAYMENT:
            return updatePayment(state, payload.paymentRequestGuid, { status: PaymentStatus.EmployeeCancelled });
        case COMPLETE_PAYMENT:
            return updatePayment(state, payload.paymentRequestGuid, {
                status: PaymentStatus.Completed,
                posRegistrationCompletedOn: payload.posRegistrationCompletedOn,
            });
        case VERIFY_PAYMENT:
            return updateVerificationItem(state, payload.paymentRequestGuid, payload.verificationGuid, { approved: payload.approved });
        case SET_PAYMENT_LOADING:
            return { ...state, hasLoadedOnce: state.hasLoadedOnce || !payload, loading: payload };
        case SHOW_IMAGES: {
            const newScreenShot = { ...payload, date: new Date() };
            const expressCheckout = _.uniqBy(
                _.orderBy([...state.expressCheckout, newScreenShot], [(x) => x.date], ['desc']),
                (x) => x.cashierId,
            );
            return { ...state, expressCheckout };
        }
        case CLEAR_IMAGES:
            return { ...state, expressCheckout: [] };
        case SET_CASHIER_GUID:
            return { ...state, cashierGuid: payload };

        case UPDATE_CUSTOMER_LABELS:
            return {
                ...state,
                payments: state.payments.map((payment) => {
                    if (payment.customer?.userGuid !== payload.customerGuid) {
                        return payment;
                    }

                    return {
                        ...payment,
                        customer: {
                            ...payment.customer,
                            labels: payload.labels.filter((label) => !label.deletedOn).map((label) => label.label),
                        },
                    };
                }),
            };

        default:
            return state;
    }
};
